import { CATEGORIES } from "../haklaiConstants";
import {
  getHaklaiPropertyDepreciationValidationError,
  getHaklaiPropertyPurchaseDepreciationResult,
  getHaklaiResidentialRentalProfile,
  hasHaklaiManualForm11DepreciationInput,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  isHaklaiManualPurchaseDepreciationAllowed,
  normalizeHaklaiDepreciationState
} from "../haklaiDepreciationHelpers";
import {
  HAKLAI_DEPRECIATION_ACTIONS,
  HAKLAI_DEPRECIATION_ERRORS
} from "../haklaiDepreciationTexts";

const PURCHASE_DEPRECIATION_EMPTY_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.configure };
const PURCHASE_DEPRECIATION_INVALID_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.check };
const PURCHASE_FORM_11_EMPTY_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.enterForm11 };
const PURCHASE_FORM_11_CONFIGURED_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.edit };
const PURCHASE_FORM_11_INVALID_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.check };
const RESIDENTIAL_RENTAL_EMPTY_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.configure };
const RESIDENTIAL_RENTAL_CONFIGURED_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.edit };
const RESIDENTIAL_RENTAL_INVALID_COPY = { actionLabel: HAKLAI_DEPRECIATION_ACTIONS.check };

export const PURCHASE_DEPRECIATION_CONFLICT_ERROR = HAKLAI_DEPRECIATION_ERRORS.purchaseDepreciationConflict;

export const getHaklaiPropertyDepreciationUiState = ({
  partKey = "",
  part = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {},
  soldPropertyDate = "",
  purchaseDepreciationStartDate = "",
  acquisitionAmount = null
} = {}) => {
  const isResidential = part?.category === CATEGORIES.RESIDENTIAL;
  const isResidentialAutoAllowed = isResidential;
  const isManualAllowed = isHaklaiManualPurchaseDepreciationAllowed(part);
  const rentalDepreciation = isResidential
    ? getHaklaiResidentialRentalProfile({
      partKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart
    })
    : {};
  const rawManualDepreciation = purchaseDepreciationByPart[partKey];
  const manualDepreciation = (
    isManualAllowed
    && hasHaklaiManualForm11DepreciationInput(rawManualDepreciation)
  )
    ? normalizeHaklaiDepreciationState(purchaseDepreciationByPart[partKey])
    : {};
  const hasRentalConfigured = Object.keys(rentalDepreciation || {}).length > 0;
  const hasManualConfigured = Object.keys(manualDepreciation || {}).length > 0;
  const hasConflict = hasRentalConfigured && hasManualConfigured;
  const rentalValidationError = hasRentalConfigured
    ? getHaklaiPropertyDepreciationValidationError({
      part,
      depreciation: rentalDepreciation,
      soldPropertyDate,
      ownershipStartDate: purchaseDepreciationStartDate,
      modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    })
    : "";
  const manualValidationError = hasManualConfigured
    ? getHaklaiPropertyDepreciationValidationError({
      part,
      depreciation: manualDepreciation,
      soldPropertyDate,
      ownershipStartDate: purchaseDepreciationStartDate,
      modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
    })
    : "";
  const rentalResult = hasRentalConfigured && !rentalValidationError
    ? getHaklaiPropertyPurchaseDepreciationResult({
      part,
      depreciation: rentalDepreciation,
      acquisitionAmount,
      ownershipStartDate: purchaseDepreciationStartDate,
      soldPropertyDate,
      modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    })
    : null;
  const manualResult = hasManualConfigured && !manualValidationError
    ? getHaklaiPropertyPurchaseDepreciationResult({
      part,
      depreciation: manualDepreciation,
      acquisitionAmount,
      soldPropertyDate,
      modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
    })
    : null;
  const hasInvalid = hasConflict || Boolean(rentalValidationError || manualValidationError);
  const hasConfigured = hasRentalConfigured || hasManualConfigured;
  const isRelevant = isResidentialAutoAllowed || isManualAllowed;
  const primaryMode = isResidentialAutoAllowed || hasRentalConfigured
    ? HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    : HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11;
  const depreciationResult = hasRentalConfigured ? rentalResult : manualResult;
  const depreciationCurrency = hasRentalConfigured
    ? rentalDepreciation.depCurrency || "newShekel"
    : manualDepreciation.depCurrency || "newShekel";
  const isComplete = isRelevant && hasConfigured && !hasInvalid;
  const validationError = hasConflict
    ? PURCHASE_DEPRECIATION_CONFLICT_ERROR
    : rentalValidationError || manualValidationError;
  const buttonCopy = hasConflict
    ? PURCHASE_DEPRECIATION_INVALID_COPY
    : hasRentalConfigured
      ? (rentalValidationError ? RESIDENTIAL_RENTAL_INVALID_COPY : RESIDENTIAL_RENTAL_CONFIGURED_COPY)
      : hasManualConfigured
        ? (manualValidationError ? PURCHASE_FORM_11_INVALID_COPY : PURCHASE_FORM_11_CONFIGURED_COPY)
        : isResidential
          ? RESIDENTIAL_RENTAL_EMPTY_COPY
          : isManualAllowed
            ? PURCHASE_FORM_11_EMPTY_COPY
            : PURCHASE_DEPRECIATION_EMPTY_COPY;

  return {
    isRelevant,
    isResidential,
    isResidentialAutoAllowed,
    isManualAllowed,
    hasConfigured,
    hasInvalid,
    isComplete,
    validationError,
    primaryMode,
    depreciationResult,
    depreciationCurrency,
    rentalDepreciation,
    manualDepreciation,
    rentalValidationError,
    manualValidationError,
    rentalResult,
    manualResult,
    hasRentalConfigured,
    hasManualConfigured,
    hasConflict,
    buttonCopy
  };
};
